import React, { useMemo, useState, useEffect } from 'react';
import { Badge, Button, Calendar, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

interface AvailabilityRow {
    date: string;
    count: number;
}

interface CalendarAvailabilityProps {
    section: any;
    data: AvailabilityRow[];
    areaName?: string;
    dateRange?: [Dayjs, Dayjs];
    onDateRangeChange?: (range: [Dayjs, Dayjs]) => void;
    disableDateChange?: boolean;
}

export const ReportAvailabilityCalendar: React.FC<CalendarAvailabilityProps> = ({
    section,
    data,
    areaName,
    dateRange,
    onDateRangeChange,
    disableDateChange = false,
}) => {
    const showCounts = section.calendarShowCounts ?? true;
    const showLegend = section.calendarShowLegend ?? true;

    const countByDate = useMemo(() => {
        const map = new Map<string, number>();
        data.forEach((row) => {
            if (!row?.date) return;
            const key = dayjs(row.date).format('YYYY-MM-DD');
            map.set(key, Number(row.count) || 0);
        });
        return map;
    }, [data]);

    const totalDays = countByDate.size;
    const totalMeasurements = useMemo(
        () => data.reduce((sum, row) => sum + (Number(row.count) || 0), 0),
        [data]
    );
    const maxCount = useMemo(() => {
        if (!data.length) return 0;
        return Math.max(...data.map((row) => Number(row.count) || 0));
    }, [data]);

    const latestDate = useMemo(() => {
        if (!data.length) return null;
        const max = data.reduce((acc, row) => {
            if (!row?.date) return acc;
            const candidate = dayjs(row.date);
            if (!candidate.isValid()) return acc;
            return !acc || candidate.isAfter(acc) ? candidate : acc;
        }, null as Dayjs | null);
        return max;
    }, [data]);

    const [leftPanelValue, setLeftPanelValue] = useState<Dayjs>(() => {
        return dateRange?.[0] ?? latestDate ?? dayjs();
    });
    const [rightPanelValue, setRightPanelValue] = useState<Dayjs>(() => {
        return dateRange?.[1] ?? latestDate ?? dayjs();
    });

    useEffect(() => {
        if (dateRange?.[0]) {
            setLeftPanelValue(dateRange[0]);
        }
        if (dateRange?.[1]) {
            setRightPanelValue(dateRange[1]);
        } else if (latestDate) {
            setRightPanelValue(latestDate);
        }
    }, [dateRange, latestDate]);

    const getIntensityStyles = (count: number, isOutsideMonth: boolean) => {
        if (!maxCount || count <= 0) {
            return {
                background: 'transparent',
                border: 'transparent',
                bar: 'transparent',
                dot: '#52c41a',
            };
        }
        const intensity = Math.min(1, count / maxCount);
        const base = isOutsideMonth ? 0.06 : 0.12;
        const scale = isOutsideMonth ? 0.2 : 0.32;
        return {
            background: `rgba(24, 144, 255, ${base + intensity * scale})`,
            border: `rgba(24, 144, 255, ${0.25 + intensity * scale})`,
            bar: `rgba(24, 144, 255, ${0.25 + intensity * (scale + 0.1)})`,
            dot: '#1677ff',
        };
    };

    const createDateFullCellRender = (panelValue: Dayjs) => (current: Dayjs) => {
        const key = current.format('YYYY-MM-DD');
        const count = countByDate.get(key) || 0;
        const isOutsideMonth = !current.isSame(panelValue, 'month');
        const styles = getIntensityStyles(count, isOutsideMonth);
        const isActive = count > 0;
        const isStart = Boolean(dateRange?.[0] && current.isSame(dateRange[0], 'day'));
        const isEnd = Boolean(dateRange?.[1] && current.isSame(dateRange[1], 'day'));
        const inRange = Boolean(
            dateRange?.[0] &&
            dateRange?.[1] &&
            (current.isAfter(dateRange[0].startOf('day')) || current.isSame(dateRange[0], 'day')) &&
            (current.isBefore(dateRange[1].endOf('day')) || current.isSame(dateRange[1], 'day'))
        );
        const rangeBackground = inRange ? 'rgba(88, 90, 95, 0.05)' : 'transparent';
        const startBackground = isActive ? 'rgba(82, 196, 26, 0.32)' : 'rgba(82, 196, 26, 0.18)';
        const endBackground = isActive ? 'rgba(250, 173, 20, 0.32)' : 'rgba(250, 173, 20, 0.18)';
        const background = isStart
            ? startBackground
            : isEnd
            ? endBackground
            : isActive
            ? styles.background
            : rangeBackground;
        const textStyle = isOutsideMonth
            ? { color: '#bfbfbf', fontWeight: isActive || isStart || isEnd ? 600 : undefined }
            : isActive
            ? { color: '#262626', fontWeight: 600 }
            : isStart || isEnd
            ? { color: '#262626', fontWeight: 600 }
            : inRange
            ? { color: '#262626' }
            : undefined;
        const showDataDot = isActive && (isStart || isEnd);
        return (
            <div className="report-calendar-cell">
                <div className="report-calendar-cell-bg" style={{ background }} />
                <div className="report-calendar-cell-value" style={textStyle}>
                    {current.date()}
                </div>
                {showDataDot && (
                    <span
                        className="report-calendar-cell-dot"
                        style={{ background: styles.dot }}
                    />
                )}
            </div>
        );
    };

    const rangeLabel = dateRange
        ? `${dateRange[0].format('YYYY-MM-DD')} → ${dateRange[1].format('YYYY-MM-DD')}`
        : null;

    const handleFromSelect = (value: Dayjs) => {
        if (disableDateChange) return;
        setLeftPanelValue(value);
        if (!onDateRangeChange) return;
        const start = value.startOf('day');
        const end = dateRange?.[1] ?? value;
        const normalizedEnd = end.isBefore(start) ? start : end;
        onDateRangeChange([start, normalizedEnd]);
    };

    const handleToSelect = (value: Dayjs) => {
        if (disableDateChange) return;
        setRightPanelValue(value);
        if (!onDateRangeChange) return;
        const end = value.endOf('day');
        const start = dateRange?.[0] ?? value;
        const normalizedStart = start.isAfter(end) ? end : start;
        onDateRangeChange([normalizedStart, end]);
    };

    const disabledDate = disableDateChange ? () => true : undefined;
    const sectionTitle = section.title || 'Dates with measurements';
    const sectionMeta = areaName && rangeLabel
        ? `${areaName} · ${rangeLabel}`
        : rangeLabel || areaName || '';

    const renderCalendarPanel = ({
        title,
        value,
        setValue,
        onSelect,
    }: {
        title: string;
        value: Dayjs;
        setValue: React.Dispatch<React.SetStateAction<Dayjs>>;
        onSelect?: (value: Dayjs) => void;
    }) => (
        <div className="report-availability-calendar-panel">
            <div className="report-availability-calendar-panel-head">
                <div className="report-availability-calendar-panel-title">{title}</div>
                <div className="report-availability-calendar-panel-controls">
                    <Button
                        size="small"
                        icon={<LeftOutlined />}
                        onClick={() => setValue((prev) => prev.subtract(1, 'month'))}
                        disabled={disableDateChange}
                    />
                    <Text strong>{value.format('MMMM YYYY')}</Text>
                    <Button
                        size="small"
                        icon={<RightOutlined />}
                        onClick={() => setValue((prev) => prev.add(1, 'month'))}
                        disabled={disableDateChange}
                    />
                </div>
            </div>
            <Calendar
                className="report-availability-calendar-widget"
                fullscreen={false}
                value={value}
                onPanelChange={(nextValue) => setValue(nextValue)}
                onSelect={disableDateChange ? undefined : onSelect}
                disabledDate={disabledDate}
                headerRender={() => null}
                dateFullCellRender={createDateFullCellRender(value)}
            />
        </div>
    );

    return (
        <div className="report-availability-calendar">
            <div className="report-availability-calendar-head">
                <div className="report-availability-calendar-title">{sectionTitle}</div>
                {sectionMeta ? <div className="report-availability-calendar-meta">{sectionMeta}</div> : null}
            </div>

            <div className="report-availability-calendar-stats">
                <span className="report-availability-calendar-pill report-availability-calendar-pill--primary">
                    Days with data: {totalDays}
                </span>
                <span className="report-availability-calendar-pill">
                    Total measurements: {totalMeasurements}
                </span>
                {maxCount > 0 ? (
                    <span className="report-availability-calendar-pill report-availability-calendar-pill--accent">
                        Max per day: {maxCount}
                    </span>
                ) : null}
                {rangeLabel ? (
                    <span className="report-availability-calendar-pill report-availability-calendar-pill--range">
                        Range: {rangeLabel}
                    </span>
                ) : null}
            </div>

            {dateRange ? (
                <div className="report-availability-calendar-grid">
                    {renderCalendarPanel({
                        title: `Date from: ${dateRange[0].format('YYYY-MM-DD')}`,
                        value: leftPanelValue,
                        setValue: setLeftPanelValue,
                        onSelect: handleFromSelect,
                    })}
                    {renderCalendarPanel({
                        title: `Date to: ${dateRange[1].format('YYYY-MM-DD')}`,
                        value: rightPanelValue,
                        setValue: setRightPanelValue,
                        onSelect: handleToSelect,
                    })}
                </div>
            ) : (
                <div className="report-availability-calendar-grid report-availability-calendar-grid--single">
                    {renderCalendarPanel({
                        title: 'Calendar availability',
                        value: rightPanelValue,
                        setValue: setRightPanelValue,
                        onSelect: handleToSelect,
                    })}
                </div>
            )}
            {showLegend && (
                <div className="report-availability-calendar-legend">
                    <Badge color="#1677ff" text="Data available" />
                    {showCounts && <Text type="secondary">Higher count = stronger highlight</Text>}
                </div>
            )}
        </div>
    );
};

export default ReportAvailabilityCalendar;
